import { Component, createRef } from 'react';
import { connect } from 'react-redux';

import { RootState, ToolTipType } from 'types';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { checkDifferent, clone } from 'utils/formHandlers';

import CoordinateTooltip from './toolTips/CoordinateToolTip';
import DrawingToolTip from './toolTips/DrawingToolTip';
import EnviromentalStatic from './toolTips/EnviromentalStatic';
import EventToolTip from './toolTips/EventToolTip';
import MeasureToolTip from './toolTips/MeasureToolTip';
import PortAreaToolTip from './toolTips/PortAreaToolTip';
import TransitToolTip from './toolTips/TransitToolTip';
import TransitToolTipStatic from './toolTips/TransitToolTipStatic';
import VesselToolTip from './toolTips/VesselToolTip';

interface ToolTipProps {
    tooltips: ToolTipType[];
    currentTime?: DateTimeFormat;
    manoeuvre?: boolean;
    mode: string;
    enviromental?: boolean;
    updateTooltips: (x: number, y: number, uuid: string | null) => void;
    updateDrawinglabel?: (id: string, name: string) => void;
}

interface ToolTipState {
    tooltips: ToolTipType[];
    currentTime: DateTimeFormat | null;
    dragging: string | null;
    dragOffset: { x: number; y: number };
}

class ToolTip extends Component<ToolTipProps, ToolTipState> {
    tooltipRefs = new Map<string, React.RefObject<HTMLDivElement>>();

    constructor(props: ToolTipProps) {
        super(props);
        this.state = {
            tooltips: clone(props.tooltips),
            dragging: null,
            currentTime: null,
            dragOffset: { x: 0, y: 0 },
        };
    }

    static getDerivedStateFromProps(props: ToolTipProps, state: ToolTipState) {
        if (props.currentTime?.toTimestamp() != state.currentTime?.toTimestamp()) {
            return { currentTime: props.currentTime };
        }

        if (checkDifferent(props.tooltips, state.tooltips)) {
            return { tooltips: clone(props.tooltips) };
        }

        return null;
    }

    createRefs = () => {
        this.state.tooltips.forEach((tooltip) => {
            this.tooltipRefs.set(tooltip.uuid, createRef<HTMLDivElement>());
        });
    };

    handleMouseMove = (event: MouseEvent) => {
        const { dragging, dragOffset } = this.state;
        if (dragging) {
            const tooltipRef = this.tooltipRefs.get(dragging);
            if (tooltipRef?.current) {
                tooltipRef.current.style.left = `${event.clientX - dragOffset.x}px`;
                tooltipRef.current.style.top = `${event.clientY - dragOffset.y}px`;
            }
        }
    };

    componentDidMount() {
        document.addEventListener('mousemove', this.handleMouseMove);
        document.addEventListener('mouseup', this.handleMouseUp);
    }

    componentWillUnmount() {
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
    }

    handleMouseDown = (event: React.MouseEvent, uuid: string) => {
        if ((event.target as HTMLElement).id === `Drag-${uuid}`) {
            const tooltipRef = this.tooltipRefs.get(uuid);

            if (tooltipRef?.current) {
                const rect = tooltipRef.current.getBoundingClientRect();
                this.setState({
                    dragging: uuid,
                    dragOffset: {
                        x: event.clientX - rect.left + 56,
                        y: event.clientY - rect.top + 56,
                    },
                });
            }
        }
    };

    handleMouseUp = () => {
        const { dragging } = this.state;
        if (dragging) {
            const tooltipRef = this.tooltipRefs.get(dragging);
            if (tooltipRef?.current) {
                const rect = tooltipRef.current.getBoundingClientRect();
                this.props.updateTooltips(rect.left - 56, rect.top - 56, dragging);
                this.setState({
                    dragging: null,
                });
            }
        }
    };

    render() {
        this.createRefs();
        return (
            <>
                {this.state.tooltips
                    .filter((x) => {
                        return x.event !== 'static';
                    })
                    .map((toolTip: ToolTipType) => {
                        let ToolTipComponent: React.ReactNode = null;
                        let ManoeuvreComponent: React.ReactNode = null;
                        let EnviromentalComponent: React.ReactNode = null;
                        switch (toolTip.type) {
                            case 'portAreas':
                                ToolTipComponent = <PortAreaToolTip key={toolTip.uuid} toolTip={toolTip} />;
                                break;
                            case 'sweptPath':
                            case 'transitTrack':
                            case 'vesselOutline':
                                ToolTipComponent = <TransitToolTip key={toolTip.uuid} toolTip={toolTip} />;
                                ManoeuvreComponent = (
                                    <TransitToolTipStatic key={`${toolTip.uuid}-manoeuvre`} toolTip={toolTip} />
                                );
                                EnviromentalComponent = (
                                    <EnviromentalStatic
                                        for="transit"
                                        key={`${toolTip.uuid}-enviromental`}
                                        toolTip={toolTip}
                                    />
                                );
                                break;

                            case 'playbackVesselOutline':
                            case 'vesselTrack':
                            case 'vesselTrackPoints':
                            case 'vesselTrackHighlightPoints':
                                ToolTipComponent = (
                                    <VesselToolTip
                                        mode={this.props.mode}
                                        currentTime={this.state.currentTime}
                                        key={toolTip.uuid}
                                        toolTip={toolTip}
                                    />
                                );

                                EnviromentalComponent = (
                                    <EnviromentalStatic
                                        for="track"
                                        key={`${toolTip.uuid}-enviromental`}
                                        toolTip={toolTip}
                                    />
                                );
                                break;
                            case 'timeVesselOutline':
                                ToolTipComponent = (
                                    <VesselToolTip
                                        mode={this.props.mode}
                                        currentTime={this.state.currentTime}
                                        key={toolTip.uuid}
                                        toolTip={toolTip}
                                    />
                                );
                                break;

                            case 'transitTrackEvents':
                                ToolTipComponent = (
                                    <EventToolTip
                                        currentTime={this.state.currentTime}
                                        key={toolTip.uuid}
                                        toolTip={toolTip}
                                    />
                                );
                                break;
                            case 'measure':
                                ToolTipComponent = (
                                    <MeasureToolTip
                                        dragging={this.state.dragging}
                                        updateDrawinglabel={this.props.updateDrawinglabel}
                                        key={toolTip.uuid}
                                        toolTip={toolTip}
                                    />
                                );
                                break;
                            case 'drawing':
                                ToolTipComponent = (
                                    <DrawingToolTip
                                        dragging={this.state.dragging}
                                        updateDrawinglabel={this.props.updateDrawinglabel}
                                        key={toolTip.uuid}
                                        toolTip={toolTip}
                                    />
                                );
                                break;
                            case 'coordinate':
                                ToolTipComponent = (
                                    <CoordinateTooltip
                                        dragging={this.state.dragging}
                                        updateDrawinglabel={this.props.updateDrawinglabel}
                                        key={toolTip.uuid}
                                        toolTip={toolTip}
                                    />
                                );
                                break;
                            default:
                                ToolTipComponent = null;
                                break;
                        }

                        return (
                            <div key={toolTip.uuid}>
                                {(this.props.manoeuvre || this.props.enviromental) &&
                                    this.props.mode !== 'playback' && (
                                        <div className="z-20 gap-2 flex absolute  right-2 top-4 flex-col">
                                            {this.props.manoeuvre && ManoeuvreComponent}
                                            {this.props.enviromental && EnviromentalComponent}{' '}
                                        </div>
                                    )}

                                <div
                                    ref={this.tooltipRefs.get(toolTip.uuid)}
                                    onMouseDown={(event: React.MouseEvent) => {
                                        this.handleMouseDown(event, toolTip.uuid);
                                    }}
                                    className="z-20 absolute bg-white no-select rounded-md mouse-pointer"
                                    style={{
                                        left: toolTip.x,
                                        top: toolTip.y,
                                    }}
                                >
                                    {ToolTipComponent}
                                </div>
                            </div>
                        );
                    })}
            </>
        );
    }
}

const toolTipStateToProps = (state: RootState) => ({
    map: state.map.map,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toolTipDispatchToProps = () => ({});

export default connect(toolTipStateToProps, toolTipDispatchToProps)(ToolTip);
